/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import isEqual from 'lodash/isEqual';

import { isArray } from '@ember/array';
import { assert } from '@ember/debug';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { TSize } from 'volta/utils/style-utils';
import type { BvLabelledSignature } from 'volta/components/bv-labelled';
import type { BvIconSignature } from 'volta/components/bv-icon';

export interface IChoiceItem {
  icon?: string;
  iconColor?: string;
  disabled?: boolean;
  title?: string;
  description?: string;
  hoveredDescription?: string;
  allowDeselection?: boolean;
  value: any;
}

export interface IChoiceSection {
  title?: string;
  description?: string;
  items: IChoiceItem[];
}

export type BvChoiceBlocksArgs = Pick<
  BvLabelledSignature['Args'],
  'label' | 'error' | 'warning' | 'helpText'
> & {
  checked?: unknown;
  auto?: boolean;
  onChange?: (value: any, e: Event) => void;
  items?: IChoiceItem[];
  sections?: IChoiceSection[];
  allowDeselection?: boolean;
  useDescriptionAsHelp?: boolean;
  maxColumns?: string;
  vertical?: boolean;
  disabled?: boolean;
  size?: Extract<TSize, 'xs' | 'sm' | 'md' | 'lg'>;
  containerClass?: string;
  iconColor?: BvIconSignature['Args']['color'];
  inlineIcon?: boolean;
};

interface BvChoiceBlocksSignature {
  Args: BvChoiceBlocksArgs;
  Blocks: {
    default: [unknown, unknown];
  };
  Element: HTMLFieldSetElement;
}

export default class BvChoiceBlocksComponent extends Component<BvChoiceBlocksSignature> {
  // eslint-disable-next-line ember/no-tracked-properties-from-args
  @tracked checked = this.args.checked;

  @tracked hoveredItem?: IChoiceItem;

  get finalSections() {
    const { items = [], sections = [] } = this.args;
    const validItems = items.filter(Boolean);
    const validSections = sections.filter((s) => s.items.length > 0);
    const finalSections: IChoiceSection[] = [];

    if (validItems.length > 0) {
      finalSections.push({ items: validItems });
    }

    assert(
      `bevolta::bv-choice-blocks - sections must be an array, you passed ${sections}`,
      isArray(sections)
    );

    if (validSections.length > 0) {
      finalSections.push(...validSections);
    }

    return finalSections;
  }

  get hasMultipleSections() {
    return this.finalSections.length > 1;
  }

  @action
  onChange(value: unknown, e: MouseEvent) {
    if (this.args.allowDeselection) {
      const deepEqual = isEqual(this.checked, value);
      if (deepEqual) {
        this.changeCheck(null, e);
      } else {
        this.changeCheck(value, e);
      }
    } else {
      this.changeCheck(value, e);
    }
  }

  @action
  handleCheckedChange(_el: HTMLElement, [value]: [any]) {
    if (this.args.auto && !isEqual(this.checked, value)) {
      this.checked = value;
    }
  }

  changeCheck(value: unknown, e: MouseEvent) {
    this.checked = value;
    this.args.onChange?.(value, e);
  }

  showDescriptionOnMouseLeave = (): boolean => {
    const { auto, checked } = this.args;
    const items = this.finalSections.flatMap((s) => s.items);
    const isChecked = auto ? this.checked : checked;
    if (isChecked) {
      return !!items.find((item: IChoiceItem) => item.value === isChecked)?.hoveredDescription;
    }
    return false;
  };
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    BvChoiceBlocks: typeof BvChoiceBlocksComponent;
    'bv-choice-blocks': typeof BvChoiceBlocksComponent;
  }
}
